// Beverage Product Builder Data Types and Constants

/**
 * @typedef {Object} Manufacturer
 * @property {string} name
 * @property {string} country
 * @property {string[]} categories
 * @property {string[]} capabilities
 * @property {string} [notes]
 */

/**
 * @typedef {Object} PackagingSupplier
 * @property {string} name
 * @property {string} country
 * @property {string[]} packagingTypes
 * @property {string[]} categories
 * @property {string} [notes]
 */

/**
 * @typedef {Object} LabelSupplier
 * @property {string} name
 * @property {string} country
 * @property {string[]} formats
 * @property {string[]} categories
 * @property {string} [notes]
 */

export const beverageCategories = [
  "Spirit",
  "Wine",
  "Sparkling",
  "RTD",
  "Water",
  "Functional Beverage",
  "Coffee",
  "Juice",
];

export const categoryOptions = {
  Spirit: {
    productTypes: ["Tequila", "Vodka", "Whiskey", "Rum", "Gin", "Liqueur", "Mezcal", "Brandy"],
    flavorFamilies: ["Citrus", "Tropical", "Coffee", "Vanilla", "Spice", "Floral", "Herbal", "Unflavored"],
    packagingTypes: ["Glass bottle", "Custom glass", "Mini bottle"],
    manufacturingPaths: ["Private label", "Full custom", "Bulk spirits"],
  },
  Wine: {
    productTypes: ["Red wine", "White wine", "Rosé", "Orange wine", "Fortified wine"],
    flavorFamilies: ["Dry", "Fruit-forward", "Oak-driven", "Mineral", "Floral", "Dessert"],
    packagingTypes: ["Glass bottle", "Lightweight bottle", "Bag-in-box", "Can"],
    manufacturingPaths: ["Custom crush", "Private label", "Bulk wine"],
  },
  Sparkling: {
    productTypes: ["Champagne-style", "Prosecco-style", "Cava-style", "Sparkling rosé", "Sparkling wine"],
    flavorFamilies: ["Brut", "Extra dry", "Rosé", "Fruit-forward", "Luxury cuvée"],
    packagingTypes: ["Glass bottle", "Mini bottle", "Can"],
    manufacturingPaths: ["Custom cuvée", "Private label", "Bulk sparkling"],
  },
  RTD: {
    productTypes: ["Cocktail RTD", "Hard seltzer", "Hard tea", "Hard coffee", "Mocktail RTD"],
    flavorFamilies: ["Citrus", "Tropical", "Berry", "Tea", "Coffee", "Spice", "Botanical"],
    packagingTypes: ["Slim can", "Standard can", "Glass bottle", "PET bottle"],
    manufacturingPaths: ["Co-pack", "Full custom", "Base + flavor blend"],
  },
  Water: {
    productTypes: ["Still water", "Sparkling water", "Alkaline water", "Mineral water", "Flavored water"],
    flavorFamilies: ["Unflavored", "Lemon", "Berry", "Tropical", "Botanical"],
    packagingTypes: ["PET bottle", "Glass bottle", "Aluminum bottle", "Can"],
    manufacturingPaths: ["Private label", "Source + bottle", "Co-pack"],
  },
  "Functional Beverage": {
    productTypes: ["Energy drink", "Adaptogen drink", "Relaxation drink", "Protein drink", "Wellness shot"],
    flavorFamilies: ["Citrus", "Berry", "Tropical", "Herbal", "Tea", "Coffee"],
    packagingTypes: ["Slim can", "Standard can", "Shot bottle", "PET bottle", "Glass bottle"],
    manufacturingPaths: ["Co-pack", "Formula development", "Private label"],
  },
  Coffee: {
    productTypes: ["Cold brew", "Latte", "Espresso RTD", "Nitro coffee", "Functional coffee"],
    flavorFamilies: ["Black", "Vanilla", "Mocha", "Caramel", "Kona-style", "Spiced"],
    packagingTypes: ["Can", "Glass bottle", "PET bottle", "Carton"],
    manufacturingPaths: ["Co-pack", "Roast + pack", "Private label"],
  },
  Juice: {
    productTypes: ["Cold-pressed juice", "Shelf-stable juice", "Smoothie", "Blend", "Concentrate-based"],
    flavorFamilies: ["Citrus", "Tropical", "Berry", "Green", "Botanical"],
    packagingTypes: ["PET bottle", "Glass bottle", "Carton", "Can"],
    manufacturingPaths: ["Co-pack", "Juice processor", "Private label"],
  },
};

/** @type {Manufacturer[]} */
export const manufacturers = [
  {
    name: "Casa Maestri / DVT",
    country: "Mexico",
    categories: ["Spirit"],
    capabilities: ["Private label", "Full custom"],
    notes: "Strong fit for tequila and agave spirits.",
  },
  {
    name: "La Cofradía",
    country: "Mexico",
    categories: ["Spirit"],
    capabilities: ["Private label", "Full custom"],
    notes: "Good fit for tequila and heritage storytelling.",
  },
  {
    name: "Infinite Agave",
    country: "Mexico",
    categories: ["Spirit"],
    capabilities: ["Private label", "Bulk spirits"],
    notes: "Good fit for agave-based projects and logistics support.",
  },
  {
    name: "Custom Crush Winery Network",
    country: "USA",
    categories: ["Wine", "Sparkling"],
    capabilities: ["Custom crush", "Private label", "Bulk wine"],
    notes: "Use for wine and sparkling brand development.",
  },
  {
    name: "Sparkling Beverage Co-Pack Group",
    country: "USA",
    categories: ["Sparkling", "RTD", "Functional Beverage"],
    capabilities: ["Co-pack", "Full custom"],
    notes: "Useful for canned or bottled sparkling concepts.",
  },
  {
    name: "RTD Beverage Co-Pack Partners",
    country: "USA",
    categories: ["RTD", "Functional Beverage", "Coffee", "Juice"],
    capabilities: ["Co-pack", "Formula development", "Private label"],
    notes: "Useful for modern canned beverages.",
  },
  {
    name: "Premium Water Source Partners",
    country: "USA",
    categories: ["Water"],
    capabilities: ["Private label", "Source + bottle", "Co-pack"],
    notes: "Useful for bottled water and alkaline water concepts.",
  },
  {
    name: "Coffee Beverage Manufacturing Group",
    country: "USA",
    categories: ["Coffee"],
    capabilities: ["Co-pack", "Roast + pack", "Private label"],
    notes: "Good for RTD coffee and functional coffee lines.",
  },
  {
    name: "Juice & Wellness Processing Partners",
    country: "USA",
    categories: ["Juice", "Functional Beverage"],
    capabilities: ["Co-pack", "Private label", "Formula development"],
    notes: "Good for juice, shots, and wellness beverages.",
  },
];

/** @type {PackagingSupplier[]} */
export const packagingSuppliers = [
  {
    name: "Saverglass Mexico",
    country: "Mexico",
    packagingTypes: ["Glass bottle", "Custom glass", "Mini bottle"],
    categories: ["Spirit", "Wine", "Sparkling"],
    notes: "Premium glass supplier for luxury beverage packaging.",
  },
  {
    name: "Vitro",
    country: "Mexico",
    packagingTypes: ["Glass bottle", "Custom glass"],
    categories: ["Spirit", "Wine", "Sparkling", "Water"],
    notes: "Strong Mexican glass option.",
  },
  {
    name: "Saverglass",
    country: "France",
    packagingTypes: ["Glass bottle", "Custom glass", "Mini bottle"],
    categories: ["Spirit", "Wine", "Sparkling"],
    notes: "High-end glass for premium positioning.",
  },
  {
    name: "Vetroelite",
    country: "Italy",
    packagingTypes: ["Glass bottle", "Custom glass"],
    categories: ["Spirit", "Wine", "Sparkling"],
    notes: "Premium design-forward bottle supplier.",
  },
  {
    name: "Zignago Vetro",
    country: "Italy",
    packagingTypes: ["Glass bottle", "Lightweight bottle"],
    categories: ["Wine", "Sparkling", "Water"],
    notes: "Strong option for wine and sparkling.",
  },
  {
    name: "CanSource Global",
    country: "USA",
    packagingTypes: ["Slim can", "Standard can", "Can"],
    categories: ["RTD", "Functional Beverage", "Coffee", "Water", "Juice"],
    notes: "Useful for canned beverage launches.",
  },
  {
    name: "Aluminum Packaging Partners",
    country: "USA",
    packagingTypes: ["Slim can", "Standard can", "Aluminum bottle"],
    categories: ["RTD", "Functional Beverage", "Water"],
    notes: "Good for modern can-based launches.",
  },
  {
    name: "PET Bottle Solutions",
    country: "USA",
    packagingTypes: ["PET bottle", "Shot bottle"],
    categories: ["Water", "Juice", "Functional Beverage", "RTD"],
    notes: "Useful for value and functional formats.",
  },
  {
    name: "Carton Pack Systems",
    country: "Canada",
    packagingTypes: ["Carton"],
    categories: ["Coffee", "Juice"],
    notes: "Useful for carton-based coffee and juice.",
  },
  {
    name: "OneGlass",
    country: "China",
    packagingTypes: ["Glass bottle", "Custom glass"],
    categories: ["Spirit", "Wine", "Sparkling"],
    notes: "Cost-flexible option for custom bottle projects.",
  },
];

/** @type {LabelSupplier[]} */
export const labelSuppliers = [
  {
    name: "CCL Wine & Spirits Mexico",
    country: "Mexico",
    formats: ["Pressure sensitive", "Necker", "Hang tag"],
    categories: ["Spirit", "Wine", "Sparkling"],
    notes: "Good fit for premium bottle-based categories.",
  },
  {
    name: "MCC",
    country: "USA",
    formats: ["Pressure sensitive", "Shrink sleeve", "Necker"],
    categories: ["Spirit", "Wine", "Sparkling", "RTD", "Water", "Functional Beverage", "Coffee", "Juice"],
    notes: "Broad beverage label capability.",
  },
  {
    name: "Resource Label Group",
    country: "USA",
    formats: ["Pressure sensitive", "Shrink sleeve"],
    categories: ["Spirit", "Wine", "RTD", "Water", "Coffee", "Juice"],
    notes: "Strong general beverage label supplier.",
  },
  {
    name: "Summit Labels",
    country: "Canada",
    formats: ["Pressure sensitive", "Shrink sleeve"],
    categories: ["RTD", "Water", "Coffee", "Juice", "Functional Beverage"],
    notes: "Useful for North American beverage projects.",
  },
  {
    name: "Spirit Labels Ltd",
    country: "UK",
    formats: ["Pressure sensitive", "Hang tag"],
    categories: ["Spirit", "Wine", "Sparkling"],
    notes: "Useful for premium spirits and wine formats.",
  },
];

// Helper functions for filtering suppliers
export const getManufacturersByCategory = (category) => {
  return manufacturers.filter(m => m.categories.includes(category));
};

export const getPackagingByCategory = (category) => {
  return packagingSuppliers.filter(p => p.categories.includes(category));
};

export const getLabelSuppliersByCategory = (category) => {
  return labelSuppliers.filter(l => l.categories.includes(category));
};

export const getManufacturersByCapability = (capability) => {
  return manufacturers.filter(m => m.capabilities.includes(capability));
};

export const getPackagingByType = (packagingType) => {
  return packagingSuppliers.filter(p => p.packagingTypes.includes(packagingType));
};
